import React, { useState } from "react"
import type { SnapshotSummaryData, SeriesRow } from "~/api/snapshot_summaries"
import { formatCurrency, formatTvpi, formatIrr, formatPercent } from "./utils"

type Entity = "company" | "series"
type Basis = "mv" | "cost"

const LOGO_COLORS = [
  "#143764",
  "#338cff",
  "#4c9c8b",
  "#c8873f",
  "#8a6fb0",
  "#c47b9a",
  "#1d7a54",
  "#b03a2e",
  "#61bffd",
]

function getInitials(name: string) {
  return name
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase()
}

function moicClass(tvpi: number | null) {
  if (tvpi == null) return ""
  if (tvpi >= 1.8) return "b-pc-moic--green"
  if (tvpi >= 1.2) return "b-pc-moic--yellow"
  return "b-pc-moic--red"
}

interface CompanyRow {
  name: string
  series_codes: string[]
  asset_class_label: string
  vintage: number
  sector: string | null
  fair_value: number
  cost: number
  dist_itd: number
  pl: number
  tvpi: number | null
  pl_pct: number | null
  portfolio_pct: number | null
}

// Coerce a value to a finite number, returning 0 for NaN/null/strings.
const safeNum = (v: unknown): number => {
  const n = Number(v)
  return isNaN(n) ? 0 : n
}

function buildCompanyRows(
  series: SeriesRow[],
  basis: Basis,
  totalMv: number | null,
  totalPl: number | null,
): CompanyRow[] {
  const map = new Map<
    string,
    {
      name: string
      series_codes: string[]
      asset_class_label: string
      vintage: number
      sector: string | null
      fair_value: number
      cost: number
      dist_itd: number
      pl: number
    }
  >()

  for (const row of series) {
    for (const asset of row.assets ?? []) {
      if (asset.is_other) continue
      const existing = map.get(asset.name)
      if (existing) {
        existing.fair_value += safeNum(asset.fair_value)
        existing.cost += safeNum(asset.cost)
        existing.dist_itd += safeNum(asset.dist_itd)
        existing.pl += safeNum(asset.pl)
        if (!existing.series_codes.includes(row.series)) {
          existing.series_codes.push(row.series)
        }
      } else {
        map.set(asset.name, {
          name: asset.name,
          series_codes: [row.series],
          asset_class_label: row.asset_class_label,
          vintage: row.vintage,
          sector: asset.sector,
          fair_value: safeNum(asset.fair_value),
          cost: safeNum(asset.cost),
          dist_itd: safeNum(asset.dist_itd),
          pl: safeNum(asset.pl),
        })
      }
    }
  }

  const rows = Array.from(map.values())
  rows.sort((a, b) =>
    basis === "mv" ? b.fair_value - a.fair_value : b.cost - a.cost,
  )

  const safeTotalPl =
    totalPl != null && !isNaN(totalPl) && totalPl !== 0 ? totalPl : null

  return rows.map((c) => ({
    ...c,
    tvpi: c.cost > 0 ? (c.fair_value + c.dist_itd) / c.cost : null,
    pl_pct: safeTotalPl != null ? c.pl / safeTotalPl : null,
    portfolio_pct:
      totalMv != null && !isNaN(totalMv) && totalMv > 0
        ? c.fair_value / totalMv
        : null,
  }))
}

interface SeriesEntry {
  series: string
  asset_class_label: string
  vintage: number
  sector: string | null
  fair_value: number
  cost: number
  irr: number | null
  tvpi: number | null
  portfolio_pct: number | null
}

function buildSeriesRows(
  series: SeriesRow[],
  basis: Basis,
  totalMv: number | null,
): SeriesEntry[] {
  const rows: SeriesEntry[] = series.map((s) => ({
    series: s.series,
    asset_class_label: s.asset_class_label,
    vintage: s.vintage,
    sector: s.sector,
    fair_value: s.performance?.mv ?? 0,
    cost: s.performance?.called ?? 0,
    irr: s.performance?.irr ?? null,
    tvpi: s.performance?.tvpi ?? null,
    portfolio_pct:
      totalMv != null && totalMv > 0
        ? (s.performance?.mv ?? 0) / totalMv
        : null,
  }))

  rows.sort((a, b) =>
    basis === "mv" ? b.fair_value - a.fair_value : b.cost - a.cost,
  )

  return rows
}

interface TopExposureTableProps {
  data: SnapshotSummaryData
}

export default function TopExposureTable({ data }: TopExposureTableProps) {
  const [entity, setEntity] = useState<Entity>("company")
  const [basis, setBasis] = useState<Basis>("mv")
  const [count, setCount] = useState<10 | 20>(10)

  const totalMv = data.totals.performance.mv
  const totalPl = data.totals.performance.pl

  const companyRows = buildCompanyRows(
    data.series,
    basis,
    totalMv ?? null,
    totalPl ?? null,
  ).slice(0, count)

  const seriesRows = buildSeriesRows(data.series, basis, totalMv ?? null).slice(
    0,
    count,
  )

  const basisLabel = basis === "mv" ? "Market Value" : "Cost"
  const maxVal =
    entity === "company"
      ? Math.max(
          ...companyRows.map((r) => (basis === "mv" ? r.fair_value : r.cost)),
          1,
        )
      : Math.max(
          ...seriesRows.map((r) => (basis === "mv" ? r.fair_value : r.cost)),
          1,
        )

  return (
    <div className="b-ps-card">
      <div className="b-ps-card__header">
        <h2>Top portfolio companies by MV exposure</h2>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <div className="b-ps-seg-toggle">
            <button
              className={`b-ps-seg-btn${entity === "company" ? " active" : ""}`}
              onClick={() => setEntity("company")}
            >
              Companies
            </button>
            <button
              className={`b-ps-seg-btn${entity === "series" ? " active" : ""}`}
              onClick={() => setEntity("series")}
            >
              Series
            </button>
          </div>
          <div className="b-ps-seg-toggle">
            <button
              className={`b-ps-seg-btn${basis === "mv" ? " active" : ""}`}
              onClick={() => setBasis("mv")}
            >
              MV
            </button>
            <button
              className={`b-ps-seg-btn${basis === "cost" ? " active" : ""}`}
              onClick={() => setBasis("cost")}
            >
              Cost
            </button>
          </div>
          <div className="b-ps-seg-toggle">
            <button
              className={`b-ps-seg-btn${count === 10 ? " active" : ""}`}
              onClick={() => setCount(10)}
            >
              Top 10
            </button>
            <button
              className={`b-ps-seg-btn${count === 20 ? " active" : ""}`}
              onClick={() => setCount(20)}
            >
              Top 20
            </button>
          </div>
        </div>
      </div>
      <div className="b-ps-card__body" style={{ padding: 0 }}>
        {entity === "company" ? (
          <CompanyTable
            rows={companyRows}
            basis={basis}
            basisLabel={basisLabel}
            maxVal={maxVal}
          />
        ) : (
          <SeriesTable
            rows={seriesRows}
            basis={basis}
            basisLabel={basisLabel}
            maxVal={maxVal}
          />
        )}
      </div>
    </div>
  )
}

function CompanyTable({
  rows,
  basis,
  basisLabel,
  maxVal,
}: {
  rows: CompanyRow[]
  basis: Basis
  basisLabel: string
  maxVal: number
}) {
  if (rows.length === 0) {
    return (
      <div className="b-ps-no-data">No portfolio company data available.</div>
    )
  }

  return (
    <div className="b-pc-table">
      <div className="b-pc-row b-pc-row--head">
        <span>#</span>
        <span />
        <span>Company</span>
        <span style={{ textAlign: "right" }}>{basisLabel}</span>
        <span style={{ textAlign: "right" }}>% P&amp;L</span>
        <span style={{ textAlign: "right" }}>MOIC</span>
        <span className="b-pc-col-hide" style={{ textAlign: "right" }}>
          % Portfolio
        </span>
      </div>
      {rows.map((row, i) => {
        const val = basis === "mv" ? row.fair_value : row.cost
        const barPct = maxVal > 0 ? (val / maxVal) * 100 : 0
        return (
          <div key={row.name} className="b-pc-row">
            <span style={{ color: "#6c7277", fontSize: 12 }}>{i + 1}</span>
            <div
              className="b-pc-logo"
              style={{ background: LOGO_COLORS[i % LOGO_COLORS.length] }}
            >
              {getInitials(row.name)}
            </div>
            <div>
              <div className="b-pc-name">{row.name}</div>
              <div className="b-pc-desc">
                {row.sector && <span className="b-pc-chip">{row.sector}</span>}
                {row.series_codes.slice(0, 2).map((sc) => (
                  <span key={sc} className="b-pc-chip b-pc-chip--series">
                    {sc}
                  </span>
                ))}
                {row.series_codes.length > 2 && (
                  <span className="b-pc-chip">
                    +{row.series_codes.length - 2}
                  </span>
                )}
              </div>
            </div>
            <div className="b-pc-num">
              {formatCurrency(val)}
              <div className="b-pc-bar-track">
                <div
                  className="b-pc-bar-fill"
                  style={{ width: `${barPct}%`, background: "#143764" }}
                />
              </div>
            </div>
            <div className="b-pc-num">
              {row.pl_pct != null ? formatPercent(row.pl_pct) : "—"}
            </div>
            <div
              className={`b-pc-num ${
                row.tvpi != null ? moicClass(row.tvpi) : ""
              }`}
            >
              {row.tvpi != null ? formatTvpi(row.tvpi) : "—"}
            </div>
            <div className="b-pc-col-hide b-pc-num">
              {row.portfolio_pct != null
                ? formatPercent(row.portfolio_pct)
                : "—"}
            </div>
          </div>
        )
      })}
    </div>
  )
}

function SeriesTable({
  rows,
  basis,
  basisLabel,
  maxVal,
}: {
  rows: SeriesEntry[]
  basis: Basis
  basisLabel: string
  maxVal: number
}) {
  if (rows.length === 0) {
    return <div className="b-ps-no-data">No series data available.</div>
  }

  return (
    <div className="b-pc-table">
      <div className="b-pc-row b-pc-row--head">
        <span>#</span>
        <span />
        <span>Series</span>
        <span style={{ textAlign: "right" }}>{basisLabel}</span>
        <span style={{ textAlign: "right" }}>IRR</span>
        <span style={{ textAlign: "right" }}>MOIC</span>
        <span className="b-pc-col-hide" style={{ textAlign: "right" }}>
          % Portfolio
        </span>
      </div>
      {rows.map((row, i) => {
        const val = basis === "mv" ? row.fair_value : row.cost
        const barPct = maxVal > 0 ? (val / maxVal) * 100 : 0
        const prefix = row.series.split(" ")[0] ?? row.series
        return (
          <div key={row.series} className="b-pc-row">
            <span style={{ color: "#6c7277", fontSize: 12 }}>{i + 1}</span>
            <div
              className="b-pc-logo"
              style={{
                background: LOGO_COLORS[i % LOGO_COLORS.length],
                fontSize: 10,
              }}
            >
              {prefix.slice(0, 4)}
            </div>
            <div>
              <div className="b-pc-name">{row.series}</div>
              <div className="b-pc-desc">
                {row.asset_class_label && (
                  <span className="b-pc-chip">{row.asset_class_label}</span>
                )}
                {row.vintage && (
                  <span className="b-pc-chip">Vintage {row.vintage}</span>
                )}
              </div>
            </div>
            <div className="b-pc-num">
              {formatCurrency(val)}
              <div className="b-pc-bar-track">
                <div
                  className="b-pc-bar-fill"
                  style={{ width: `${barPct}%`, background: "#143764" }}
                />
              </div>
            </div>
            <div className="b-pc-num">{formatIrr(row.irr)}</div>
            <div
              className={`b-pc-num ${
                row.tvpi != null ? moicClass(row.tvpi) : ""
              }`}
            >
              {row.tvpi != null ? formatTvpi(row.tvpi) : "—"}
            </div>
            <div className="b-pc-col-hide b-pc-num">
              {row.portfolio_pct != null
                ? formatPercent(row.portfolio_pct)
                : "—"}
            </div>
          </div>
        )
      })}
    </div>
  )
}
